import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraCheckbox, SpecraFormField, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { projectDataClient } from '../../lib/connect/clients';
import { CAPTURE_KEY } from './hooks';
import { invalidatePursuitReads } from './record/pursuitQueryKeys';
import { ReadOnlyScope } from './record/ReadOnlyScope';
export function lifecycleLabel(value) {
    return ({
        active: 'Active',
        parked: 'Parked',
        closed: 'Closed'
    })[value] ?? 'Unknown';
}
export function businessPhaseLabel(value) {
    return ({
        shaping: 'Shaping',
        qualifying: 'Qualifying',
        pursuing: 'Pursuing'
    })[value] ?? 'Unknown';
}
const ACTION_LABELS = {
    park: 'Park',
    close: 'Close',
    resume: 'Resume',
    record_outcome: 'Record outcome'
};
export function CaptureLifecyclePanel({ summary, tasks, initialAction, readOnly = false }) {
    const queryClient = useQueryClient();
    const key = [
        CAPTURE_KEY,
        summary.captureProjectId
    ];
    const history = useQuery({
        queryKey: [
            ...key,
            'lifecycle-events'
        ],
        queryFn: async ()=>(await projectDataClient.listCaptureLifecycleEvents({
                captureProjectId: summary.captureProjectId
            })).events,
        retry: false,
        enabled: !initialAction
    });
    const mutation = useMutation({
        mutationFn: async (request)=>{
            const response = await projectDataClient.changeCaptureLifecycle(request);
            if (!response.summary || !response.event) throw new Error('Incomplete lifecycle response');
            return response;
        },
        retry: false,
        onSuccess: async ()=>{
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: key
                }, {
                    throwOnError: true
                }),
                queryClient.invalidateQueries({
                    queryKey: [
                        'capture-list'
                    ]
                }),
                // GRW-12/13: lifecycle labels and the thread's lifecycle event.
                invalidatePursuitReads(queryClient)
            ]);
        }
    });
    const [action, setAction] = useState(initialAction ?? null);
    const [reason, setReason] = useState('');
    const [revisit, setRevisit] = useState('');
    const [outcome, setOutcome] = useState('');
    const [selected, setSelected] = useState([]);
    // Freeze the entire request, including its revision, on first send. An uncertain
    // response permits only an identical retry, even if a refetch updates the summary.
    const pending = useRef(null);
    const [failure, setFailure] = useState(null);
    const [message, setMessage] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const busy = mutation.isPending || refreshing;
    const frozen = busy || pending.current !== null;
    const canPause = action === 'park' || action === 'close';
    const canAct = [
        'active',
        'parked',
        'closed'
    ].includes(summary.lifecycle);
    const dateValid = !revisit || Number.isFinite(new Date(`${revisit}T00:00:00`).getTime());
    const reset = ()=>{
        pending.current = null;
        setAction(null);
        setReason('');
        setRevisit('');
        setOutcome('');
        setSelected([]);
        setFailure(null);
    };
    const begin = (next)=>{
        reset();
        setMessage('');
        setAction(next);
    };
    const submit = async ()=>{
        var _pending;
        if (!action || busy || failure && failure !== 'uncertain' || !reason.trim() || !dateValid || action === 'record_outcome' && !outcome.trim()) return;
        (_pending = pending).current ?? (_pending.current = {
            captureProjectId: summary.captureProjectId,
            requestId: crypto.randomUUID(),
            expectedRevision: summary.lifecycleRevision,
            action,
            reason: reason.trim(),
            revisitAt: canPause && revisit ? Timestamp.fromDate(new Date(`${revisit}T00:00:00`)) : undefined,
            outcome: action === 'record_outcome' || action === 'close' ? outcome.trim() : '',
            pauseTaskIds: canPause ? [
                ...selected
            ] : []
        });
        try {
            await mutation.mutateAsync(pending.current);
            reset();
            setMessage(`${ACTION_LABELS[action]} recorded. Capture content remains editable.`);
        } catch (error) {
            const code = ConnectError.from(error).code;
            setFailure(code === Code.Aborted ? 'conflict' : code === Code.PermissionDenied || code === Code.Unauthenticated ? 'denied' : [
                Code.InvalidArgument,
                Code.FailedPrecondition,
                Code.NotFound
            ].includes(code) ? 'rejected' : 'uncertain');
        }
    };
    const refresh = async ()=>{
        setRefreshing(true);
        try {
            await queryClient.refetchQueries({
                queryKey: key,
                type: 'active'
            }, {
                throwOnError: true
            });
            reset();
            setMessage('Refreshed. Review the current lifecycle before choosing an action.');
        } catch  {
            setMessage('Refresh failed. Try refreshing again.');
        } finally{
            setRefreshing(false);
        }
    };
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs(SpecraCard, {
            "data-testid": Testids.captureLifecycle.panel,
            children: [
                /*#__PURE__*/ _jsx(SpecraCardHeader, {
                    children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                        children: "Pursuit lifecycle"
                    })
                }),
                /*#__PURE__*/ _jsxs(SpecraCardContent, {
                    className: "space-y-3",
                    children: [
                        /*#__PURE__*/ _jsxs("p", {
                            "data-testid": Testids.captureLifecycle.state,
                            children: [
                                "Lifecycle: ",
                                lifecycleLabel(summary.lifecycle)
                            ]
                        }),
                        summary.lifecycleRevisitAt && /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "Revisit: ",
                                summary.lifecycleRevisitAt.toDate().toLocaleDateString()
                            ]
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Capture content stays editable. Only explicitly selected open tasks are paused. Resume does not resume tasks. Linked workspaces and Watches keep their own state."
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap gap-2",
                            children: [
                                summary.lifecycle === 'active' && /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    disabled: action !== null || busy,
                                    onClick: ()=>begin('park'),
                                    "data-testid": Testids.captureLifecycle.park,
                                    children: "Park"
                                }),
                                (summary.lifecycle === 'active' || summary.lifecycle === 'parked') && /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    disabled: action !== null || busy,
                                    onClick: ()=>begin('close'),
                                    "data-testid": Testids.captureLifecycle.close,
                                    children: "Close"
                                }),
                                (summary.lifecycle === 'parked' || summary.lifecycle === 'closed') && /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    disabled: action !== null || busy,
                                    onClick: ()=>begin('resume'),
                                    "data-testid": Testids.captureLifecycle.resume,
                                    children: "Resume"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    disabled: !canAct || action !== null || busy,
                                    onClick: ()=>begin('record_outcome'),
                                    "data-testid": Testids.captureLifecycle.recordOutcome,
                                    children: "Record outcome"
                                })
                            ]
                        }),
                        action && /*#__PURE__*/ _jsxs("form", {
                            className: "space-y-3",
                            onSubmit: (event)=>{
                                event.preventDefault();
                                void submit();
                            },
                            children: [
                                /*#__PURE__*/ _jsxs("h3", {
                                    className: "font-medium",
                                    children: [
                                        ACTION_LABELS[action],
                                        " Pursuit"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Reason",
                                    required: true,
                                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        required: true,
                                        value: reason,
                                        disabled: frozen,
                                        onChange: (event)=>setReason(event.target.value),
                                        "data-testid": Testids.captureLifecycle.reason
                                    })
                                }),
                                canPause && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Revisit date (optional)",
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                type: "date",
                                                value: revisit,
                                                disabled: frozen,
                                                onChange: (event)=>setRevisit(event.target.value),
                                                "data-testid": Testids.captureLifecycle.revisit
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs("fieldset", {
                                            disabled: frozen,
                                            className: "space-y-2",
                                            children: [
                                                /*#__PURE__*/ _jsx("legend", {
                                                    children: "Pause selected open tasks (optional)"
                                                }),
                                                tasks.filter((task)=>task.status === 'open' || task.status === 'in_progress').map((task)=>/*#__PURE__*/ _jsx(SpecraFormField, {
                                                        label: `${task.title} (${task.status === 'in_progress' ? 'In progress' : 'Open'})`,
                                                        children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                                            disabled: frozen,
                                                            checked: selected.includes(task.id),
                                                            "data-testid": Testids.captureLifecycle.task,
                                                            "data-task-id": task.id,
                                                            onCheckedChange: (checked)=>setSelected(checked === true ? [
                                                                    ...selected,
                                                                    task.id
                                                                ] : selected.filter((id)=>id !== task.id))
                                                        })
                                                    }, task.id)),
                                                /*#__PURE__*/ _jsx("p", {
                                                    children: "No tasks are selected by default. Other tasks keep their current state."
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                (action === 'record_outcome' || action === 'close') && /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: action === 'record_outcome' ? 'Outcome' : 'Outcome (optional)',
                                    required: action === 'record_outcome',
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        required: action === 'record_outcome',
                                        value: outcome,
                                        disabled: frozen,
                                        onChange: (event)=>setOutcome(event.target.value),
                                        "data-testid": Testids.captureLifecycle.outcome
                                    })
                                }),
                                action === 'record_outcome' && /*#__PURE__*/ _jsx("p", {
                                    children: "Recording an outcome does not change this Pursuit's lifecycle or linked workspaces."
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            type: "submit",
                                            disabled: busy || !reason.trim() || !dateValid || action === 'record_outcome' && !outcome.trim() || failure !== null && failure !== 'uncertain',
                                            "data-testid": Testids.captureLifecycle.submit,
                                            children: busy ? 'Saving…' : failure === 'uncertain' ? 'Retry same action' : `Confirm ${ACTION_LABELS[action]}`
                                        }),
                                        !pending.current && /*#__PURE__*/ _jsx(SpecraButton, {
                                            type: "button",
                                            variant: "secondary",
                                            disabled: busy,
                                            onClick: reset,
                                            "data-testid": Testids.captureLifecycle.cancel,
                                            children: "Cancel"
                                        })
                                    ]
                                })
                            ]
                        }),
                        failure && /*#__PURE__*/ _jsxs("div", {
                            role: "alert",
                            "data-testid": Testids.captureLifecycle.error,
                            children: [
                                failure === 'conflict' ? 'This Pursuit changed. Refresh and review before trying again.' : failure === 'denied' ? 'You do not have permission to change this Pursuit. Refresh to check access.' : failure === 'rejected' ? 'The action was rejected. Refresh and review the lifecycle and selected tasks before trying again.' : 'The result could not be confirmed. Retry the same action to safely retrieve its result.',
                                failure !== 'uncertain' && /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    disabled: busy,
                                    onClick: ()=>void refresh(),
                                    "data-testid": Testids.captureLifecycle.refresh,
                                    children: "Refresh lifecycle"
                                })
                            ]
                        }),
                        message && /*#__PURE__*/ _jsx("output", {
                            className: "block",
                            children: message
                        }),
                        !initialAction && /*#__PURE__*/ _jsxs("section", {
                            "aria-label": "Lifecycle and outcome history",
                            "data-testid": Testids.captureLifecycle.history,
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "font-medium",
                                    children: "Lifecycle and outcome history"
                                }),
                                history.isLoading ? /*#__PURE__*/ _jsx("p", {
                                    children: "Loading lifecycle history…"
                                }) : history.isError ? /*#__PURE__*/ _jsxs("div", {
                                    role: "alert",
                                    children: [
                                        "Lifecycle history unavailable.",
                                        ' ',
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "secondary",
                                            onClick: ()=>void history.refetch(),
                                            children: "Retry history"
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        history.data?.length === 0 && /*#__PURE__*/ _jsx("p", {
                                            children: "No lifecycle events recorded."
                                        }),
                                        /*#__PURE__*/ _jsx("ol", {
                                            className: "space-y-2",
                                            children: history.data?.map((event)=>/*#__PURE__*/ _jsxs("li", {
                                                    "data-testid": Testids.captureLifecycle.event,
                                                    "data-event-id": event.id,
                                                    className: "border-b py-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            children: [
                                                                event.createdAt?.toDate().toLocaleString() ?? 'Date unavailable',
                                                                " ·",
                                                                ' ',
                                                                event.action === 'record_outcome' ? 'Record outcome' : ACTION_LABELS[event.action] ?? event.action,
                                                                ' ',
                                                                "· ",
                                                                lifecycleLabel(event.fromLifecycle),
                                                                " →",
                                                                ' ',
                                                                lifecycleLabel(event.toLifecycle)
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            children: [
                                                                "Actor: ",
                                                                event.actorUserId || 'Unavailable',
                                                                " · Reason:",
                                                                ' ',
                                                                event.reason || 'Not recorded'
                                                            ]
                                                        }),
                                                        event.revisitAt && /*#__PURE__*/ _jsxs("p", {
                                                            children: [
                                                                "Revisit: ",
                                                                event.revisitAt.toDate().toLocaleDateString()
                                                            ]
                                                        }),
                                                        event.outcome && /*#__PURE__*/ _jsxs("p", {
                                                            children: [
                                                                "Outcome: ",
                                                                event.outcome
                                                            ]
                                                        }),
                                                        event.legacyStage !== undefined && /*#__PURE__*/ _jsxs("p", {
                                                            children: [
                                                                "Legacy stage: ",
                                                                event.legacyStage
                                                            ]
                                                        }),
                                                        event.pausedTaskIds.length > 0 && /*#__PURE__*/ _jsxs("p", {
                                                            children: [
                                                                "Paused task IDs: ",
                                                                event.pausedTaskIds.join(', ')
                                                            ]
                                                        })
                                                    ]
                                                }, event.id))
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
